import { SidebarContext, Theme, defaultMenuConfig } from '@pinchenterprisesnpm/friday-ui'
import { GlobalStyles } from 'config/GlobalStyles.js'
import { SetIsAuthUser, SetUserData } from 'data/Auth/actions'
import { isDev, isDevelopment, isIntegration } from 'helpers/helperFunctions'
import { history } from 'helpers/history'
import MonitoringService from 'helpers/monitoringService'
import { IUser } from 'models/User/index'
import React, { Component, createContext, useContext } from 'react'
import { ReactNotifications } from 'react-notifications-component'
import { connect } from 'react-redux'
import { Redirect, Route, Router, Switch } from 'react-router-dom'
import ROUTES from 'routing/adminRoutes'
import AuthService from 'services/AuthService'
import store from 'store/store'
import { BaseCSS, GridThemeProvider } from 'styled-bootstrap-grid'
import { ThemeProvider } from 'styled-components'
import AdminRouteLayout from 'ui/components/Admin/AdminRouteLayout'
import AdminRoute from 'ui/components/common/AdminRoute'
import ErrorBoundary from 'ui/components/common/ErrorBoundary'
import { redirectOldBusinessEntityRoutes } from 'ui/components/common/RedirectUser'
import activeShiftsRoutes from 'ui/components/common/Routes/ActiveShiftsRoutes'
import businessesRoutes from 'ui/components/common/Routes/BusinessesRoutes'
import changePasswordRoutes from 'ui/components/common/Routes/ChangePasswordRoutes'
import featuredShiftsRoutes from 'ui/components/common/Routes/FeaturedShiftsRoutes'
import financeRoutes from 'ui/components/common/Routes/FinanceRoutes'
import followManagementRoutes from 'ui/components/common/Routes/FollowManagementRoutes'
import globalSettingsRoutes from 'ui/components/common/Routes/GlobalSettingsRoutes'
import locationsRoutes from 'ui/components/common/Routes/LocationsRoutes'
import onboarderRoutes from 'ui/components/common/Routes/OnboarderRoutes'
import reportRoutes from 'ui/components/common/Routes/ReportsRoutes'
import shiftRoutes from 'ui/components/common/Routes/ShiftsRoutes'
import usersRoutes from 'ui/components/common/Routes/UsersRoutes'
import { ScrollToTop } from 'ui/components/common/ScrollToTop'
import {
  AsyncLogin,
  AsyncLogout,
  AsyncPasswordRecovery,
  AsyncTwoFactorAuthentication,
} from 'ui/pages'
import certificationRequestsRoutes from 'ui/components/common/Routes/CertificationRequestsRoutes'
import { UpdateShiftPostingRedesignFlag } from 'ui/components/Admin/UpdateShiftPostingRedesignFlag'
import { ModalsRoot } from 'helpers/modals'

import 'react-notifications-component/dist/theme.css'

const gridTheme = {
  row: {
    padding: 10, // default 15
  },
  col: {
    padding: 5, // default 15
  },
  // container: {
  //   padding: 0,
  // },
}

// tslint:disable-next-line: no-unused-expression
if (!isDevelopment() || !isDev() || !isIntegration()) {
  MonitoringService.initialization()
}

// Authentication
const loggedInUser = localStorage.getItem('loggedInUser')
const token = localStorage.getItem('token')

if (token && loggedInUser) {
  store.dispatch(SetUserData(JSON.parse(loggedInUser)))
  store.dispatch(SetIsAuthUser())
}

export const SidebarUpdateContext = createContext({
  forceUpdate: () => {},
})

export const useSidebarUpdateContext = () => useContext(SidebarUpdateContext)

type TAppProps = {}

type TAppState = { isCollapsed: boolean; menuWidth: string }

class App extends Component<TAppProps, TAppState> {
  constructor(props: any) {
    super(props)

    const menuState = localStorage.getItem('menuState')

    this.state = {
      isCollapsed: menuState && menuState === 'collapsed' ? true : false,
      menuWidth:
        menuState && menuState === 'collapsed'
          ? defaultMenuConfig.COLLAPSED
          : defaultMenuConfig.EXPANDED,
    }
  }

  toggleMenuType = () => {
    this.setState((state) => ({
      isCollapsed: !state.isCollapsed,
      menuWidth:
        state.menuWidth === defaultMenuConfig.COLLAPSED
          ? defaultMenuConfig.EXPANDED
          : defaultMenuConfig.COLLAPSED,
    }))
  }

  componentDidMount() {
    const menuState = localStorage.getItem('menuState')

    this.setState({
      isCollapsed: menuState && menuState === 'collapsed' ? true : false,
      menuWidth:
        menuState && menuState === 'collapsed'
          ? defaultMenuConfig.COLLAPSED
          : defaultMenuConfig.EXPANDED,
    })

    if (AuthService.getLoggedInUser()) {
      const loggedInUser = AuthService.getLoggedInUser() as IUser
      MonitoringService.setUser(loggedInUser)
    }
  }

  componentDidUpdate(_: TAppProps, prevState: TAppState) {
    const menuState = localStorage.getItem('menuState')
    const isNewCollapsed = menuState && menuState === 'collapsed' ? true : false

    if (prevState.isCollapsed !== isNewCollapsed) {
      this.setState({
        isCollapsed: isNewCollapsed,
        menuWidth: isNewCollapsed ? defaultMenuConfig.COLLAPSED : defaultMenuConfig.EXPANDED,
      })
    }
  }

  render() {
    const sidebarContextValue = {
      isCollapsed: this.state.isCollapsed,
      menuWidth: this.state.menuWidth,
      toggleMenuType: this.toggleMenuType,
    }

    return (
      <ThemeProvider theme={Theme}>
        <ModalsRoot />
        <ReactNotifications className='notifications-upshift' />
        <GridThemeProvider gridTheme={gridTheme}>
          <ErrorBoundary>
            <React.Fragment>
              <UpdateShiftPostingRedesignFlag />
              <BaseCSS />
              <GlobalStyles />
              <SidebarUpdateContext.Provider
                value={{
                  forceUpdate: () => this.forceUpdate(),
                }}
              >
                <SidebarContext.Provider value={sidebarContextValue}>
                  <Router history={history}>
                    <ScrollToTop />
                    <Switch>
                      {redirectOldBusinessEntityRoutes}
                      {shiftRoutes}
                      {activeShiftsRoutes}
                      {financeRoutes}
                      {usersRoutes}
                      {businessesRoutes}
                      {/* Location types and Categories are breaking if the routes  are placed under locations routes */}
                      {globalSettingsRoutes}
                      {locationsRoutes}
                      {reportRoutes}
                      {featuredShiftsRoutes}
                      {onboarderRoutes}
                      {followManagementRoutes}
                      {changePasswordRoutes}
                      {certificationRequestsRoutes}
                      <Route exact path='/2fa' component={AsyncTwoFactorAuthentication} />
                      <AdminRoute
                        exact
                        path={ROUTES.LOGOUT}
                        component={AsyncLogout}
                        layout={AdminRouteLayout}
                      />
                      <Route
                        exact
                        path={ROUTES.PASSWORD_RECOVERY}
                        component={AsyncPasswordRecovery}
                      />
                      <Route exact path={ROUTES.ROOT} component={AsyncLogin} />
                      <Redirect from='/' to={ROUTES.SHIFTS} />
                    </Switch>
                  </Router>
                </SidebarContext.Provider>
              </SidebarUpdateContext.Provider>
            </React.Fragment>
          </ErrorBoundary>
        </GridThemeProvider>
      </ThemeProvider>
    )
  }
}

export default connect()(App)
